import { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBusinessSubscription,
  updateBusinessSubscription,
  deleteBusinessOverride,
  fetchAdminPlans,
  fetchAdminFeatures,
  createBusinessOverride,
  getAdminVoiceDebug,
  connectAdminVapiAssistant,
  disconnectAdminVapiAssistant,
} from "../../api/admin";
import type { AdminFeature, FeatureOverride } from "../../api/admin";
import EntitlementUsage from "../../components/EntitlementUsage";

const STATUS_OPTIONS = ["active", "trialing", "manual", "past_due", "canceled", "unpaid"] as const;
const OVERRIDEABLE_FEATURE_KEYS = new Set([
  "web_chat",
  "web_chat_conversations",
  "public_booking_page",
  "voice_ai",
  "voice_seconds",
  "sms_notifications",
  "sms_messages",
  "bookings_created",
  "calendar_connections",
]);

type OverrideType = "force_enabled" | "force_disabled" | "custom_limit";
type OverrideFormMode = "create" | "edit";
type OverrideFormState = {
  featureId: string;
  overrideType: OverrideType;
  limit: string;
  expiresAt: string;
  reason: string;
};

function formatDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function toDateInputValue(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function overrideTypeLabel(type: OverrideType): string {
  if (type === "custom_limit") return "custom limit";
  if (type === "force_enabled") return "enabled";
  return "disabled";
}

function overrideOptionLabel(type: OverrideType): string {
  if (type === "custom_limit") return "Custom limit";
  if (type === "force_enabled") return "Force enabled";
  return "Force disabled";
}

function overrideValue(override: FeatureOverride): number | null {
  return override.override_value ?? override.override_limit_quantity ?? null;
}

function getAllowedOverrideTypes(featureType: AdminFeature["feature_type"] | undefined): OverrideType[] {
  if (featureType === "binary") return ["force_enabled", "force_disabled"];
  if (featureType === "metered" || featureType === "metered_period" || featureType === "capacity") return ["custom_limit", "force_disabled"];
  return [];
}

function initialOverrideForm(feature: AdminFeature | undefined, override?: FeatureOverride): OverrideFormState {
  const allowedTypes = getAllowedOverrideTypes(feature?.feature_type);
  const overrideType = override?.override_type ?? allowedTypes[0] ?? "custom_limit";
  const value = override ? overrideValue(override) : null;
  return {
    featureId: override?.feature_id ?? feature?.feature_id ?? "",
    overrideType,
    limit: value == null ? "" : String(value),
    expiresAt: toDateInputValue(override?.expires_at ?? null),
    reason: override?.reason ?? "",
  };
}

function validateOverrideForm(form: OverrideFormState): Partial<Record<keyof OverrideFormState, string>> {
  const errors: Partial<Record<keyof OverrideFormState, string>> = {};
  if (!form.featureId) errors.featureId = "Feature is required.";
  if (!form.reason.trim()) errors.reason = "Reason is required.";
  if (form.overrideType === "custom_limit") {
    const limit = Number(form.limit);
    if (!form.limit.trim() || !Number.isInteger(limit) || limit < 0) {
      errors.limit = "Limit must be a non-negative whole number.";
    }
  }
  return errors;
}

export default function AdminBusinessDetailDrawer({
  businessId,
  onClose,
}: {
  businessId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "business", businessId],
    queryFn: () => getBusinessSubscription(businessId!),
    enabled: !!businessId,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: fetchAdminPlans,
  });

  const { data: adminFeatures = [] } = useQuery({
    queryKey: ["admin", "features"],
    queryFn: fetchAdminFeatures,
  });

  const { data: voiceDebug, isError: voiceDebugError } = useQuery({
    queryKey: ["admin", "voice", businessId],
    queryFn: () => getAdminVoiceDebug(businessId!),
    enabled: !!businessId,
  });

  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideDialogMode, setOverrideDialogMode] = useState<OverrideFormMode>("create");
  const [editingOverride, setEditingOverride] = useState<FeatureOverride | null>(null);
  const [overrideForm, setOverrideForm] = useState<OverrideFormState>(() => initialOverrideForm(undefined));
  const [overrideErrors, setOverrideErrors] = useState<Partial<Record<keyof OverrideFormState, string>>>({});
  const [removeOverride, setRemoveOverride] = useState<FeatureOverride | null>(null);

  const sub = data?.subscription;
  const overrides = data?.overrides ?? [];
  const entitlementFeatures = data?.features ?? [];
  const business = data?.business;
  const overrideableFeatures = adminFeatures.filter((feature) => OVERRIDEABLE_FEATURE_KEYS.has(feature.feature_key) && feature.is_active);
  const selectedOverrideFeature = overrideableFeatures.find((feature) => feature.feature_id === overrideForm.featureId);
  const selectedOverrideTypes = getAllowedOverrideTypes(selectedOverrideFeature?.feature_type);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateBusinessSubscription(businessId!, {
        plan_id: selectedPlanId || sub?.plan_id || undefined,
        status: selectedStatus || sub?.status || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "business", businessId] });
      qc.invalidateQueries({ queryKey: ["admin", "businesses"] });
    },
  });

  const createOverrideMutation = useMutation({
    mutationFn: async () => {
      if (editingOverride) {
        await deleteBusinessOverride(businessId!, editingOverride.override_id);
      }
      const limit = overrideForm.overrideType === "custom_limit" ? Number(overrideForm.limit) : null;
      return createBusinessOverride(businessId!, {
        feature_id: overrideForm.featureId,
        override_type: overrideForm.overrideType,
        override_limit_quantity: limit,
        override_value: limit,
        expires_at: overrideForm.expiresAt || null,
        reason: overrideForm.reason.trim(),
      });
    },
    onSuccess: () => {
      setOverrideDialogOpen(false);
      setEditingOverride(null);
      setOverrideErrors({});
      qc.invalidateQueries({ queryKey: ["admin", "business", businessId] });
    },
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (overrideId: string) => deleteBusinessOverride(businessId!, overrideId),
    onSuccess: () => {
      setRemoveOverride(null);
      qc.invalidateQueries({ queryKey: ["admin", "business", businessId] });
    },
  });

  const connectVoiceMutation = useMutation({
    mutationFn: (force: boolean) => connectAdminVapiAssistant(businessId!, force),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "voice", businessId] });
    },
  });

  const disconnectVoiceMutation = useMutation({
    mutationFn: () => disconnectAdminVapiAssistant(businessId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "voice", businessId] });
    },
  });

  const effectivePlanId = selectedPlanId || sub?.plan_id || "";
  const effectiveStatus = selectedStatus || sub?.status || "";
  const isDirty = (selectedPlanId && selectedPlanId !== sub?.plan_id) || (selectedStatus && selectedStatus !== sub?.status);

  function openCreateOverrideDialog() {
    const firstFeature = overrideableFeatures[0];
    setOverrideDialogMode("create");
    setEditingOverride(null);
    setOverrideForm(initialOverrideForm(firstFeature));
    setOverrideErrors({});
    createOverrideMutation.reset();
    setOverrideDialogOpen(true);
  }

  function openEditOverrideDialog(override: FeatureOverride) {
    const feature = overrideableFeatures.find((item) => item.feature_id === override.feature_id);
    setOverrideDialogMode("edit");
    setEditingOverride(override);
    setOverrideForm(initialOverrideForm(feature, override));
    setOverrideErrors({});
    createOverrideMutation.reset();
    setOverrideDialogOpen(true);
  }

  function updateOverrideFeature(featureId: string) {
    const feature = overrideableFeatures.find((item) => item.feature_id === featureId);
    setOverrideForm(initialOverrideForm(feature, overrides.find((override) => override.feature_id === featureId)));
    setOverrideErrors({});
    createOverrideMutation.reset();
  }

  function updateOverrideType(overrideType: OverrideType) {
    setOverrideForm((current) => ({
      ...current,
      overrideType,
      limit: overrideType === "custom_limit" ? current.limit : "",
    }));
    setOverrideErrors((current) => ({ ...current, overrideType: undefined, limit: undefined }));
    createOverrideMutation.reset();
  }

  function updateOverrideField(field: keyof OverrideFormState, value: string) {
    setOverrideForm((current) => ({ ...current, [field]: value }));
    setOverrideErrors((current) => ({ ...current, [field]: undefined }));
    createOverrideMutation.reset();
  }

  function submitOverrideForm() {
    const errors = validateOverrideForm(overrideForm);
    setOverrideErrors(errors);
    if (Object.keys(errors).length > 0) return;
    createOverrideMutation.mutate();
  }

  return (
    <Drawer
      anchor="right"
      open={!!businessId}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, backgroundColor: "#fff", borderLeft: "1px solid #E2E8F0" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid #F1F5F9" }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>
            {business?.name ?? "Business"}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8", fontFamily: "monospace" }}>{businessId}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {isLoading && <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={24} sx={{ color: "#C9734F" }} /></Box>}
        {isError && <Alert severity="error">Failed to load subscription data.</Alert>}

        {data && (
          <>
            {/* Subscription section */}
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Subscription
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={effectivePlanId}
                  label="Plan"
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                >
                  <MenuItem value=""><em>No plan</em></MenuItem>
                  {plans.map((p) => (
                    <MenuItem key={p.plan_id} value={p.plan_id}>{p.display_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={effectiveStatus}
                  label="Status"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {sub?.current_period_start && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mb: 0.5 }}>Period start</Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#0F172A" }}>{sub.current_period_start}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mb: 0.5 }}>Period end</Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#0F172A" }}>{sub.current_period_end}</Typography>
                  </Box>
                </Box>
              )}

              {isDirty && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}
                >
                  {updateMutation.isPending ? "Saving…" : "Save changes"}
                </Button>
              )}
              {updateMutation.isError && <Alert severity="error" sx={{ mt: 1 }}>Failed to update subscription.</Alert>}
              {updateMutation.isSuccess && <Alert severity="success" sx={{ mt: 1 }}>Subscription updated.</Alert>}

              {(sub?.stripe_customer_id || sub?.stripe_subscription_id) && (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {sub?.stripe_customer_id && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Stripe Customer</Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#475569", backgroundColor: "#F1F5F9", px: 0.75, py: 0.25, borderRadius: "4px" }}>
                        {sub.stripe_customer_id}
                      </Typography>
                    </Box>
                  )}
                  {sub?.stripe_subscription_id && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Stripe Sub</Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#475569", backgroundColor: "#F1F5F9", px: 0.75, py: 0.25, borderRadius: "4px" }}>
                        {sub.stripe_subscription_id}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: "#F1F5F9", mb: 3 }} />

            <EntitlementUsage features={entitlementFeatures} />

            <Divider sx={{ borderColor: "#F1F5F9", my: 3 }} />

            {/* Voice debug section */}
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Voice Assistant Debug
            </Typography>

            {voiceDebugError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Voice diagnostics are not available.
              </Alert>
            )}

            {voiceDebug && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Status</Typography>
                  <Chip
                    label={voiceDebug.status}
                    size="small"
                    sx={{
                      backgroundColor: voiceDebug.status === "connected" ? "#F0FDF4" : "#F8FAFC",
                      color: voiceDebug.status === "connected" ? "#16A34A" : "#64748B",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Model</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569", fontFamily: "monospace" }}>
                    {voiceDebug.model_provider || "not set"} / {voiceDebug.model || "not set"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Assistant ID</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569", fontFamily: "monospace", wordBreak: "break-all" }}>
                    {voiceDebug.vapi_assistant_id ?? "not created"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Phone ID</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569", fontFamily: "monospace", wordBreak: "break-all" }}>
                    {voiceDebug.vapi_phone_number_id ?? "not configured"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Tools</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569" }}>
                    {voiceDebug.tool_id_completeness.configured_count} / {voiceDebug.tool_id_completeness.required_count} configured
                  </Typography>
                </Box>
                {voiceDebug.tool_id_completeness.missing.length > 0 && (
                  <Typography sx={{ fontSize: "0.72rem", color: "#DC2626" }}>
                    Missing: {voiceDebug.tool_id_completeness.missing.join(", ")}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Sync hash</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569", fontFamily: "monospace", wordBreak: "break-all" }}>
                    {voiceDebug.sync_hash ?? "none"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", minWidth: 110 }}>Last synced</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#475569" }}>
                    {voiceDebug.last_synced_at ?? "never"}
                  </Typography>
                </Box>
                {voiceDebug.sync_error && (
                  <Alert severity="error">{voiceDebug.sync_error}</Alert>
                )}
                <TextField
                  label="First message preview"
                  value={voiceDebug.first_message_preview}
                  multiline
                  minRows={2}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Generated prompt preview"
                  value={voiceDebug.generated_prompt_preview}
                  multiline
                  minRows={4}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={connectVoiceMutation.isPending}
                    onClick={() => connectVoiceMutation.mutate(false)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                  >
                    Sync
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={connectVoiceMutation.isPending}
                    onClick={() => connectVoiceMutation.mutate(true)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                  >
                    Force sync
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={disconnectVoiceMutation.isPending}
                    onClick={() => disconnectVoiceMutation.mutate()}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                  >
                    Disconnect
                  </Button>
                </Box>
                {connectVoiceMutation.isError && (
                  <Alert severity="error">Failed to sync voice assistant.</Alert>
                )}
                {disconnectVoiceMutation.isError && (
                  <Alert severity="error">Failed to disconnect voice assistant.</Alert>
                )}
              </Box>
            )}

            <Divider sx={{ borderColor: "#F1F5F9", my: 3 }} />

            {/* Overrides section */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Feature Overrides
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={openCreateOverrideDialog}
                disabled={overrideableFeatures.length === 0}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
              >
                Add override
              </Button>
            </Box>

            {deleteOverrideMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to remove override.
              </Alert>
            )}

            {overrides.length === 0 ? (
              <Typography sx={{ fontSize: "0.82rem", color: "#94A3B8" }}>No overrides set for this business.</Typography>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Feature</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", color: "#64748B" }}>Limit</TableCell>
                      <TableCell sx={{ width: 72 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overrides.map((ov) => (
                      <TableRow key={ov.override_id} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#0F172A", py: 1 }}>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{ov.feature_name}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", fontFamily: "monospace" }}>{ov.feature_key}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={overrideTypeLabel(ov.override_type)}
                            size="small"
                            sx={{
                              backgroundColor: ov.override_type === "force_disabled" ? "#FEF2F2" : "#F0FDF4",
                              color: ov.override_type === "force_disabled" ? "#DC2626" : "#16A34A",
                              fontSize: "0.68rem",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#64748B" }}>
                          {overrideValue(ov)?.toLocaleString() ?? "—"}
                          {ov.reason && (
                            <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", mt: 0.4 }}>
                              {ov.reason}
                            </Typography>
                          )}
                          {ov.expires_at && (
                            <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", mt: 0.4 }}>
                              Expires {formatDate(ov.expires_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <IconButton
                            size="small"
                            aria-label={`Edit ${ov.feature_name} override`}
                            sx={{ color: "#64748B" }}
                            onClick={() => openEditOverrideDialog(ov)}
                          >
                            <EditRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${ov.feature_name} override`}
                            sx={{ color: "#DC2626" }}
                            disabled={deleteOverrideMutation.isPending}
                            onClick={() => setRemoveOverride(ov)}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Override entitlement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth error={!!overrideErrors.featureId}>
              <InputLabel id="override-feature-label">Feature</InputLabel>
              <Select
                labelId="override-feature-label"
                label="Feature"
                value={overrideForm.featureId}
                onChange={(event) => updateOverrideFeature(event.target.value)}
                disabled={overrideDialogMode === "edit"}
              >
                {overrideableFeatures.map((feature) => (
                  <MenuItem key={feature.feature_id} value={feature.feature_id}>
                    {feature.display_name}
                  </MenuItem>
                ))}
              </Select>
              {overrideErrors.featureId && (
                <Typography sx={{ color: "#DC2626", fontSize: "0.72rem", mt: 0.5 }}>{overrideErrors.featureId}</Typography>
              )}
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="override-type-label">Override type</InputLabel>
              <Select
                labelId="override-type-label"
                label="Override type"
                value={overrideForm.overrideType}
                onChange={(event) => updateOverrideType(event.target.value as OverrideType)}
              >
                {selectedOverrideTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {overrideOptionLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {overrideForm.overrideType === "custom_limit" && (
              <TextField
                label="Limit"
                size="small"
                value={overrideForm.limit}
                onChange={(event) => updateOverrideField("limit", event.target.value)}
                error={!!overrideErrors.limit}
                helperText={overrideErrors.limit}
                fullWidth
              />
            )}

            <TextField
              label="Expires"
              size="small"
              type="date"
              value={overrideForm.expiresAt}
              onChange={(event) => updateOverrideField("expiresAt", event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Reason"
              size="small"
              value={overrideForm.reason}
              onChange={(event) => updateOverrideField("reason", event.target.value)}
              error={!!overrideErrors.reason}
              helperText={overrideErrors.reason}
              multiline
              minRows={3}
              fullWidth
            />

            {createOverrideMutation.isError && (
              <Alert severity="error">
                Failed to save override.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitOverrideForm}
            disabled={createOverrideMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {createOverrideMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removeOverride} onClose={() => setRemoveOverride(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove override</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: "#475569" }}>
            Remove the override for {removeOverride?.feature_name}? This business will return to its plan-derived entitlement state.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOverride(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => removeOverride && deleteOverrideMutation.mutate(removeOverride.override_id)}
            disabled={deleteOverrideMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Remove override
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
